import { readClientEnv } from '../config/runtimeEnv';

const WEATHER_CACHE_TTL_MS = 2 * 60 * 1000;
const WEATHER_TIMEOUT_MS = 10 * 1000;

let cachedWeather = null;
let cachedAt = 0;
let inFlightRequest = null;

const validateWeather = (data) => {
  if (
    !data ||
    typeof data.current !== 'object' ||
    !Array.isArray(data.forecast?.forecastday)
  ) {
    throw new Error('Weather service returned an unexpected payload.');
  }
  return data;
};

const withCallerAbort = (promise, signal) => {
  if (!signal) return promise;
  if (signal.aborted) return Promise.reject(new DOMException('Weather request aborted.', 'AbortError'));

  return new Promise((resolve, reject) => {
    const onAbort = () => reject(new DOMException('Weather request aborted.', 'AbortError'));
    signal.addEventListener('abort', onAbort, { once: true });
    promise.then(
      value => {
        signal.removeEventListener('abort', onAbort);
        resolve(value);
      },
      error => {
        signal.removeEventListener('abort', onAbort);
        reject(error);
      },
    );
  });
};

const fetchWeather = async () => {
  const url = readClientEnv('REACT_APP_WEATHER_URL').trim() || '/api/weather';
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), WEATHER_TIMEOUT_MS);

  try {
    const response = await fetch(url, { method: 'GET', signal: controller.signal });
    if (!response.ok) {
      throw new Error(`Weather service returned HTTP ${response.status}.`);
    }

    const contentType = response.headers.get('content-type') || '';
    if (!contentType.toLowerCase().includes('application/json')) {
      throw new Error('Weather service returned a non-JSON response.');
    }

    return validateWeather(await response.json());
  } finally {
    clearTimeout(timeout);
  }
};

export function clearWeatherCache() {
  cachedWeather = null;
  cachedAt = 0;
  inFlightRequest = null;
}

export function isWeatherCacheFresh(now = Date.now()) {
  return Boolean(cachedWeather && now - cachedAt < WEATHER_CACHE_TTL_MS);
}

export async function getWeather({ force = false, signal } = {}) {
  if (!force && isWeatherCacheFresh()) return cachedWeather;

  if (!inFlightRequest) {
    inFlightRequest = fetchWeather()
      .then((data) => {
        cachedWeather = data;
        cachedAt = Date.now();
        return data;
      })
      .finally(() => {
        inFlightRequest = null;
      });
  }

  return withCallerAbort(inFlightRequest, signal);
}
